import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  articlePublishedTime?: string;
  articleModifiedTime?: string;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = "website",
  articlePublishedTime,
  articleModifiedTime,
}: SEOHeadProps) {
  useEffect(() => {
    document.title = title;
    updateMetaTag("description", description);
    updateMetaTag("keywords", keywords);
    updateLink("canonical", canonicalUrl);
    updateMetaTag("og:title", ogTitle || title);
    updateMetaTag("og:description", ogDescription || description);
    updateMetaTag("og:type", ogType);
    updateMetaTag("og:url", canonicalUrl);
    if (ogImage) updateMetaTag("og:image", ogImage);
    updateMetaTag("twitter:card", "summary_large_image");
    updateMetaTag("twitter:title", ogTitle || title);
    updateMetaTag("twitter:description", ogDescription || description);
    if (ogImage) updateMetaTag("twitter:image", ogImage);

    if (articlePublishedTime) {
      updateMetaTag("article:published_time", articlePublishedTime);
    }
    if (articleModifiedTime) {
      updateMetaTag("article:modified_time", articleModifiedTime);
    }

    return () => {
      // Clean up article-specific meta on unmount
      if (articlePublishedTime) removeMetaTag("article:published_time");
      if (articleModifiedTime) removeMetaTag("article:modified_time");
    };
  }, [
    title, description, keywords, canonicalUrl,
    ogTitle, ogDescription, ogImage, ogType,
    articlePublishedTime, articleModifiedTime,
  ]);

  return null;
}

function updateMetaTag(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.querySelector(`meta[property="${name}"]`) as HTMLMetaElement | null;
  }
  if (el) {
    el.setAttribute("content", content);
  }
}

function removeMetaTag(name: string) {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.querySelector(`meta[property="${name}"]`) as HTMLMetaElement | null;
  }
  if (el) {
    el.remove();
  }
}

function updateLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (el) {
    el.setAttribute("href", href);
  }
}